import React, { useContext, useRef } from 'react';
import type { ChildrenProp, ClassNameProp } from '../common-types';
import type { CommonInputProps } from './CommonInputProps';
import { BoxLayout } from '../layout';
import { once, uniqueId } from 'lodash';
import { ValidationErrorMessage } from './internal';

/**
 * Type used by the Context created through {@link createRadioGroupContext}.
 * @typeParam T the type of the 'value'
 * @author agd
 */
type RadioGroupContextType<T> =
  | {
      value: T | null;
      onChange: CommonInputProps<T>['onChange'];
      onBlur?: CommonInputProps<T>['onBlur'];
      onFocus?: CommonInputProps<T>['onFocus'];
      valueComparator?: (contextValue?: T | null, value?: T) => boolean;
      /*
       * A radio group is truly grouped only if it is within a component with role "radiogroup" and all of the "radio"-components have the same name.
       *
       * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/radiogroup_role
       * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#name
       */
      name: string;
    }
  | undefined;

/**
 * Creates a new context with the {@link RadioGroupContextType} typing.
 * @typeParam T type parameter that will be passed to the {@link RadioGroupContextType} type
 * @author agd
 */
const createRadioGroupContext = once(<T,>() =>
  React.createContext<RadioGroupContextType<T>>(undefined)
);

/**
 * Hook to use the context created by a {@link RadioGroup} component.
 * @typeParam the type of the 'value' property passed to a {@link RadioGroup}.
 * @author agd
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useRadioGroupContext = <T,>() =>
  useContext<RadioGroupContextType<T>>(createRadioGroupContext<T>());

/**
 * Props for a {@link RadioGroup} component.
 * @typeParam T the type of the value
 * @author agd
 */
export interface RadioGroupProps<T>
  extends Required<ChildrenProp>,
    ClassNameProp,
    Omit<
      CommonInputProps<T>,
      'debounceWait' | 'defaultValue' | 'value' | 'label'
    > {
  /**
   * Label of the Radio group.
   */
  label?: string;
  /**
   * If displayed as a row or not.
   */
  row?: boolean;
  /**
   * Optional function to use to determine if a Radio is checked or not.
   * If this option is not passed, then a simple object comparison is used.
   * @param contextValue current value in context
   * @param value value to compare the state to
   * @return true if equal according to the comparison, else false
   */
  valueComparator?: (contextValue?: T | null, value?: T) => boolean;
  /** @see {@link CommonInputProps.value } */
  value: T | null;
  /** @see {@link CommonInputProps.onChange } */
  onChange: Exclude<CommonInputProps<T>['onChange'], undefined>;
}

/**
 * Component to manage multiple Radio components as a single group using an
 * arbitrarily typed value to determine which Radio is checked.
 *
 * @typeParam T value type
 * @param properties a {@link RadioGroupProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function RadioGroup<T>({
  row = false,
  labelOnTop = false,
  label = '',
  className = '',
  ...props
}: RadioGroupProps<T>): JSX.Element {
  const RadioGroupContext = createRadioGroupContext<T>();
  const wrapperClassName = `ehs-radio-group-wrapper ${className}`.trim();
  const groupClassName = `ehs-radio-group ${
    props.error ? 'invalid' : 'valid'
  }`.trim();
  // change handler
  const onChange = (val: T) => {
    props.onChange(val);
  };
  const uniqueRadioGroupId = useRef(uniqueId('ehs-react-radio-group'));

  const isLabelVisible = label.length > 0;
  return (
    <RadioGroupContext.Provider
      value={{
        value: props.value,
        onChange: onChange,
        onBlur: props.onBlur,
        onFocus: props.onFocus,
        valueComparator: props.valueComparator,
        name: uniqueRadioGroupId.current
      }}
    >
      <div className={wrapperClassName}>
        {!!row && labelOnTop && (
          <RadioLabel
            label={label}
            required={props.required ?? false}
            id={uniqueRadioGroupId.current}
            isLabelVisible={isLabelVisible}
          />
        )}
        <BoxLayout
          className={groupClassName}
          variant={row ? 'hbox' : 'vbox'}
          role="radiogroup"
          aria-labelledby={
            isLabelVisible ? uniqueRadioGroupId.current : undefined
          }
        >
          {(!labelOnTop || !row) && (
            <RadioLabel
              label={label}
              required={props.required ?? false}
              id={uniqueRadioGroupId.current}
              isLabelVisible={isLabelVisible}
            />
          )}
          {props.children}
        </BoxLayout>

        {props.error && <ValidationErrorMessage error={props.error} />}
      </div>
    </RadioGroupContext.Provider>
  );
}

interface RadioLabelProps {
  label: string;
  required: boolean;
  id: string;
  isLabelVisible: boolean;
}

function RadioLabel({
  label,
  required,
  isLabelVisible,
  id
}: Readonly<RadioLabelProps>): JSX.Element {
  if (!isLabelVisible) {
    return <></>;
  }

  return (
    <h3 id={id} className="radio-group-label">
      {label} {`${required ? '*' : ''}`}
    </h3>
  );
}
